import { toRFC3339 } from "../utils/time";
import { fetchJson } from "./http";
import type { ServiceSlug, Span, SpanStatistics } from "./types";

export interface GetSpansParams {
  serviceSlug: ServiceSlug;
  startTime: Date;
  endTime: Date;
  serviceNames?: string[];
  serviceInstances?: string[];
  spanNames?: string[];
  traceIds?: string[];
  spanIds?: string[];
  minDurationMs?: number | null;
  maxDurationMs?: number | null;
  isRoot?: boolean | null;
  sort?: string;
  order?: string;
  limit?: number;
  signal?: AbortSignal;
}

export async function getSpans({
  serviceSlug,
  startTime,
  endTime,
  serviceNames = [],
  serviceInstances = [],
  spanNames = [],
  traceIds = [],
  spanIds = [],
  minDurationMs,
  maxDurationMs,
  isRoot,
  sort,
  order,
  limit,
  signal,
}: GetSpansParams): Promise<Span[]> {
  const result = await fetchJson<Span[] | null>("/spans", {
    serviceSlug,
    signal,
    searchParams: {
      start_time: toRFC3339(startTime),
      end_time: toRFC3339(endTime),
      service_names: serviceNames.length > 0 ? serviceNames.join(",") : undefined,
      service_instances: serviceInstances.length > 0 ? serviceInstances.join(",") : undefined,
      span_names: spanNames.length > 0 ? spanNames.join(",") : undefined,
      traceIds: traceIds.length > 0 ? traceIds.join(",") : undefined,
      spanIds: spanIds.length > 0 ? spanIds.join(",") : undefined,
      min_duration_ms: minDurationMs != null ? minDurationMs : undefined,
      max_duration_ms: maxDurationMs != null ? maxDurationMs : undefined,
      is_root: isRoot === true ? "true" : isRoot === false ? "false" : undefined,
      sort,
      order,
      limit,
    },
  });

  return Array.isArray(result) ? result : [];
}

export async function getSpan(
  spanId: string,
  serviceSlug: ServiceSlug,
  signal?: AbortSignal,
): Promise<Span | null> {
  return fetchJson<Span | null>(`/spans/${encodeURIComponent(spanId)}`, {
    serviceSlug,
    signal,
  });
}

export type SpanStatisticsBaselineWindow = "1h" | "24h" | "7d" | "30d";

export interface GetSpanStatisticsParams {
  serviceSlug: ServiceSlug;
  startTime: Date;
  endTime: Date;
  serviceName?: string;
  serviceInstance?: string;
  spanName?: string;
  traceIds?: string[];
  spanIds?: string[];
  baseline?: SpanStatisticsBaselineWindow;
  groupBy?: "service_name,span_name";
  limit?: number;
  signal?: AbortSignal;
}

export async function getSpanStatistics({
  serviceSlug,
  startTime,
  endTime,
  serviceName,
  serviceInstance,
  spanName,
  traceIds,
  spanIds,
  baseline,
  groupBy,
  limit,
  signal,
}: GetSpanStatisticsParams): Promise<SpanStatistics[]> {
  const result = await fetchJson<SpanStatistics[] | null>("/spans/statistics", {
    serviceSlug,
    signal,
    searchParams: {
      start_time: toRFC3339(startTime),
      end_time: toRFC3339(endTime),
      service_name: serviceName,
      service_instance: serviceInstance,
      span_name: spanName,
      traceIds: traceIds && traceIds.length > 0 ? traceIds.join(",") : undefined,
      spanIds: spanIds && spanIds.length > 0 ? spanIds.join(",") : undefined,
      baseline,
      group_by: groupBy,
      limit,
    },
  });

  return Array.isArray(result) ? result : [];
}
